import React, { ReactElement } from "react"
import { Link } from "gatsby"
import Layout from "../../components/layout"
import { ValidatorFunction, CodeExample } from "../../components/validatorFuncs"
import InlineCode from "../../components/inlineCode"
import Examples from "../../components/codeExamples"

function StringFunctions(): ReactElement {
  return (
    <Layout
      title="String functions"
      description="Validation functions for use on strings"
    >
      <h1>String Functions</h1>
      <p>
        <ul className="list">
          {[
            "NotEmpty",
            "Empty",
            "Regex",
            "StartsWith",
            "EndsWith",
            "Contains",
            "Length",
            "MinLength",
            "MaxLength",
            "Required",
            "Email",
          ].map((func) => (
            <li>
              <Link to={`/functions/string/#${func}`}>{func}</Link>
            </li>
          ))}
        </ul>
      </p>
      <hr className="my-6" />
      <ValidatorFunction
        name="NotEmpty"
        description="Ensures the string value being validated is not empty."
        methods={[
          `NotEmpty()`,
          `NotEmpty<T>(Expression<Func<T, string>> selector)`,
        ]}
        brokenRule="Value cannot be empty."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.NotEmpty.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.NotEmpty.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.NotEmpty.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Empty"
        description="Ensures the string value being validated is empty."
        methods={[`Empty()`, `Empty<T>(Expression<Func<T, string>> selector)`]}
        brokenRule="Value must be empty."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Empty.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Empty.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Empty.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Regex"
        description="Ensures the string value being validated matches a regular expression pattern."
        methods={[
          `Regex(string pattern)`,
          `Regex(string pattern, RegexOptions options)`,
          `Regex<T>(Expression<Func<T, string>> selector, string pattern)`,
          `Regex<T>(Expression<Func<T, string>> selector, string pattern, RegexOptions options)`,
        ]}
        brokenRule="Value must match pattern."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Regex.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Regex.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Regex.Example3}
        />
        <CodeExample
          label="Using RegexOptions."
          code={Examples.String.Regex.Example4}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="StartsWith"
        description="Ensures the string value being validated starts with the specified value."
        methods={[
          `StartsWith(string value)`,
          `StartsWith<T>(Expression<Func<T, string>> selector, string value)`,
        ]}
        brokenRule="Value must start with '{value}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.StartsWith.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.StartsWith.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.StartsWith.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="EndsWith"
        description="Ensures the string value being validated ends with the specified value."
        methods={[
          `EndsWith(string value)`,
          `EndsWith<T>(Expression<Func<T, string>> selector, string value)`,
        ]}
        brokenRule="Value must end with '{value}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.EndsWith.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.EndsWith.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.EndsWith.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Contains"
        description="Ensures the string value being validated contains the specified value."
        methods={[
          `Contains(string value)`,
          `Contains<T>(Expression<Func<T, string>> selector, string value)`,
        ]}
        brokenRule="Value must contain '{value}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Contains.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Contains.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Contains.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Length"
        description="Ensures the string value being validated has a length within the specified range."
        methods={[
          `Length(int min, int max)`,
          `Length<T>(Expression<Func<T, string>> selector, int min, int max)`,
        ]}
        brokenRule="Value must be {min} to {max} characters in length."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Length.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Length.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Length.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="MinLength"
        description="Ensures the string value being validated has a minimum length."
        methods={[
          `MinLength(int min)`,
          `MinLength<T>(Expression<Func<T, string>> selector, int min)`,
        ]}
        brokenRule="Value must have minimum length of {min}."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.MinLength.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.MinLength.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.MinLength.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="MaxLength"
        description="Ensures the string value being validated has a maximum length."
        methods={[
          `MaxLength(int max)`,
          `MaxLength<T>(Expression<Func<T, string>> selector, int max)`,
        ]}
        brokenRule="Value must have maximum length of {max}."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.MaxLength.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.MaxLength.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.MaxLength.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Required"
        description="Ensures the string value being validated is not null or empty or whitespace."
        methods={[
          `Required()`,
          `Required<T>(Expression<Func<T, string>> selector)`,
        ]}
        brokenRule="Value is required."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Required.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Required.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Required.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Email"
        description="Ensures the string value being validated is an email address."
        methods={[`Email()`, `Email<T>(Expression<Func<T, string>> selector)`]}
        brokenRule="Value must be a valid email."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.String.Email.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.String.Email.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.String.Email.Example3}
        />
      </ValidatorFunction>
    </Layout>
  )
}

export default StringFunctions
